/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import {
  InvitationApiError,
  InvitationFormErrors,
  INVITATION_ERROR_MESSAGES,
} from '../types/invitation';

/**
 * Parses RTK Query error responses for invitation operations
 * and converts them to user-friendly form field errors
 */
export function parseInvitationError(error: unknown): InvitationFormErrors {
  const formErrors: InvitationFormErrors = {};

  // Handle RTK Query error format
  if (error && typeof error === 'object' && 'data' in error) {
    const apiError = error as InvitationApiError;
    const errorData = apiError.data;

    // Handle field-specific validation errors
    if (errorData.email && Array.isArray(errorData.email)) {
      const emailError = errorData.email[0];

      // Check for specific error messages and provide user-friendly versions
      if (emailError.includes('already a member')) {
        formErrors.email = INVITATION_ERROR_MESSAGES.ALREADY_MEMBER;
      } else if (emailError.includes('pending invitation')) {
        formErrors.email = INVITATION_ERROR_MESSAGES.PENDING_INVITATION;
      } else {
        formErrors.email = emailError;
      }
    }

    if (errorData.role && Array.isArray(errorData.role)) {
      const roleError = errorData.role[0];

      // Check for role permission errors
      if (
        roleError.includes('cannot invite') ||
        roleError.includes('permission')
      ) {
        formErrors.role = INVITATION_ERROR_MESSAGES.ROLE_PERMISSION;
      } else {
        formErrors.role = roleError;
      }
    }

    if (errorData.custom_message && Array.isArray(errorData.custom_message)) {
      const [messageError] = errorData.custom_message;
      formErrors.message = messageError;
    }

    // Handle non-field errors
    if (
      errorData.non_field_errors &&
      Array.isArray(errorData.non_field_errors)
    ) {
      const [generalError] = errorData.non_field_errors;
      formErrors.general = generalError;
    }

    // Handle general error messages
    if (
      !formErrors.email &&
      !formErrors.role &&
      !formErrors.message &&
      !formErrors.general
    ) {
      if (errorData.detail) {
        formErrors.general = errorData.detail;
      } else if (errorData.message) {
        formErrors.general = errorData.message;
      }
    }
  }

  // Handle network errors
  if (error && typeof error === 'object' && 'status' in error) {
    const statusError = error as { status: string | number };

    if (
      statusError.status === 'FETCH_ERROR' ||
      statusError.status === 'TIMEOUT_ERROR'
    ) {
      formErrors.general = INVITATION_ERROR_MESSAGES.NETWORK_ERROR;
    }
  }

  // Fallback for unknown errors
  if (Object.keys(formErrors).length === 0) {
    formErrors.general = INVITATION_ERROR_MESSAGES.GENERIC_ERROR;
  }

  return formErrors;
}

/**
 * Checks if an error is specifically the "already a member" error
 */
export function isAlreadyMemberError(error: unknown): boolean {
  const formErrors = parseInvitationError(error);
  return formErrors.email === INVITATION_ERROR_MESSAGES.ALREADY_MEMBER;
}

/**
 * Checks if an error is specifically the "pending invitation" error
 */
export function isPendingInvitationError(error: unknown): boolean {
  const formErrors = parseInvitationError(error);
  return formErrors.email === INVITATION_ERROR_MESSAGES.PENDING_INVITATION;
}

/**
 * Gets a user-friendly error message for display in alerts
 * Prioritizes field-specific errors over general errors
 */
export function getDisplayErrorMessage(error: unknown): string {
  const formErrors = parseInvitationError(error);

  // Return the first field-specific error found
  if (formErrors.email) return formErrors.email;
  if (formErrors.role) return formErrors.role;
  if (formErrors.message) return formErrors.message;

  // Fall back to general error
  return formErrors.general || INVITATION_ERROR_MESSAGES.GENERIC_ERROR;
}

/**
 * Gets actionable guidance for specific error scenarios
 */
export function getErrorGuidance(error: unknown): string | null {
  if (isAlreadyMemberError(error)) {
    return 'You can manage their role and permissions in the Members tab.';
  }

  if (isPendingInvitationError(error)) {
    return 'You can resend or cancel the existing invitation in the Invitations tab.';
  }

  return null;
}
